import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TextField,
  Typography,
} from "@mui/material";
import { Edit, Save } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectAuth } from "../auth/authSlice";
import { ClientTypes } from "../auth/types/authType";
import Toast from "../../components/Layout/Toast";
import TableSkeleton from "../../components/Layout/TableSkeleton";
import { StyledTableCell, StyledTableRow } from "../../styles/tableStyles";
import { addCompany, getCompany, selectSetups } from "./setupSlices";
import { Company } from "./types/setupTypes";

export const PartnerRates = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { user } = useAppSelector(selectAuth);
  const { loading, error, success, selectedCompany } =
    useAppSelector(selectSetups);

  const companyId = user?.company?._id as string | undefined;
  const [editing, setEditing] = useState<Company | null>(null);
  const [amount, setAmount] = useState("");

  useEffect(() => {
    if (companyId) {
      dispatch(getCompany(companyId));
    }
  }, [dispatch, companyId]);

  const partners = (
    selectedCompany?._id === companyId ? selectedCompany.partners ?? [] : []
  ).filter((p) => p.type === ClientTypes.ForeignAgent);

  const openEdit = (partner: Company) => {
    setEditing(partner);
    setAmount(
      partner.owedAmountPerEmployee != null
        ? String(partner.owedAmountPerEmployee)
        : "",
    );
  };

  const closeEdit = () => {
    setEditing(null);
    setAmount("");
  };

  const handleSave = async () => {
    if (!editing) return;
    const result = await dispatch(
      addCompany({
        _id: editing._id,
        type: editing.type,
        displayName: editing.displayName,
        email: editing.email,
        owedAmountPerEmployee: Number(amount),
      }),
    );
    if (companyId) {
      dispatch(getCompany(companyId));
    }
    if (addCompany.fulfilled.match(result)) {
      closeEdit();
    }
  };

  return (
    <>
      <Helmet>
        <title>{`Partner Rates | ${t("platform")}`}</title>
      </Helmet>
      <Box sx={{ m: { xs: 0, md: 1 }, px: { xs: 0, md: 2 } }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Partner Rates
        </Typography>
        <Typography color="textSecondary" sx={{ mb: 2 }}>
          Default USD amount charged per employee for each foreign agent
          partner.
        </Typography>

        {loading === "pending" && <LinearProgress color="secondary" />}
        {success && <Toast severity="success">{success.message}</Toast>}
        {error && <Toast severity="error">{error.message}</Toast>}

        <TableContainer component={Paper}>
          <Table size="small" aria-label="partner rates">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>S.No</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Email</StyledTableCell>
                <StyledTableCell>Amount (USD)</StyledTableCell>
                <StyledTableCell>Actions</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {loading === "pending" && selectedCompany?._id !== companyId ? (
                <TableSkeleton numRows={5} numColumns={5} />
              ) : (
                <>
                  {partners.map((row, index) => (
                    <StyledTableRow key={row._id ?? index}>
                      <StyledTableCell>{index + 1}</StyledTableCell>
                      <StyledTableCell>{row.displayName}</StyledTableCell>
                      <StyledTableCell>{row.email}</StyledTableCell>
                      <StyledTableCell>
                        {row.owedAmountPerEmployee != null
                          ? row.owedAmountPerEmployee
                          : "—"}
                      </StyledTableCell>
                      <StyledTableCell>
                        <IconButton
                          color="primary"
                          onClick={() => openEdit(row)}
                          size="large"
                        >
                          <Edit />
                        </IconButton>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                  {partners.length === 0 && (
                    <StyledTableRow>
                      <StyledTableCell colSpan={5}>
                        No foreign agent partners found.
                      </StyledTableCell>
                    </StyledTableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog open={editing !== null} onClose={closeEdit} fullWidth maxWidth="xs">
        <DialogTitle>
          {editing ? `Edit rate — ${editing.displayName}` : "Edit rate"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Amount charged per employee (USD)"
            type="number"
            fullWidth
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            variant="outlined"
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>Cancel</Button>
          <Button
            color="secondary"
            variant="contained"
            disabled={loading === "pending" || !(Number(amount) > 0)}
            onClick={handleSave}
          >
            <Save /> {t("save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
